/* eslint-disable react/jsx-props-no-spreading */
import Column from '@amzn/meridian/column';
import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik';
import React from 'react';
import {
  CoachingDetailsInput,
  CoachingFormData,
  CoachingFormInputData,
  ContactInput,
} from '../../../../api/CoachingForm/schema';
import CheckboxGroupWithContactDetails from '../../FieldComponents/CheckboxGroupWithContactDetails';
import { FieldTooltips } from '../Context';
import { ConsolidatedContact } from '../../../../api/CoachingNotes/schema';
import {
  getContactInput,
  parseConsolidatedContactToContactGetter,
} from './Util/helper';
import { filterContact } from '../Subform';

interface SubformProps {
  onsubmit: Function;
  initialValues: ContactInput[];
  contactList: ConsolidatedContact[];
  disabledTooltips: FieldTooltips;
  coachingNote: CoachingFormData;
}

type SubformContentProps = FormikProps<ContactInput[]> & {
  contactList: SubformProps['contactList'];
  contactReview: ContactInput[];
  disabledTooltips: SubformProps['disabledTooltips'];
  handleSubmitWithValues: (input: ContactInput[]) => void;
  coachingDetails: CoachingDetailsInput[];
};

const SubformContent: React.FC<SubformContentProps> = ({
  contactList,
  handleSubmitWithValues,
  contactReview,
  coachingDetails,
}: SubformContentProps): React.ReactElement => {
  return (
    <Form>
      <Column spacing='400'>
        <Field
          as={CheckboxGroupWithContactDetails}
          name='contacts[]'
          options={contactList}
          values={contactReview ?? []}
          details={coachingDetails ?? []}
          onChange={(value: { target: { value: string[] } }): boolean => {
            const contactInputList = getContactInput(
              value.target.value,
              contactList
            );
            handleSubmitWithValues(contactInputList);
            return true;
          }}
        />
      </Column>
    </Form>
  );
};

const Subform = ({
  onsubmit,
  initialValues,
  contactList,
  disabledTooltips,
  coachingNote,
}: SubformProps): React.ReactElement => {
  const formattedContactList =
    parseConsolidatedContactToContactGetter(contactList);

  const {
    values: { contactReview, coachingDetails },
  }: FormikProps<CoachingFormInputData> = useFormikContext();

  const handleSubmit = (values: ContactInput[]): void => {
    let contactMap = new Map<String, ContactInput>();

    values.forEach(value => {
      const sumbitValue = filterContact(
        formattedContactList,
        value,
        coachingNote
      );

      if (!contactMap.has(sumbitValue.cscContactID)) {
        contactMap.set(sumbitValue.cscContactID, sumbitValue);
      }
    });

    onsubmit(Array.from(contactMap.values()));
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {(props: FormikProps<ContactInput[]>): React.ReactElement => (
        <>
          <SubformContent
            {...props}
            contactList={contactList}
            contactReview={contactReview}
            disabledTooltips={disabledTooltips}
            handleSubmitWithValues={handleSubmit}
            coachingDetails={coachingDetails}
          />
        </>
      )}
    </Formik>
  );
};

export default Subform;
